import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { useAgreement } from "../../context/AgreementContext";
import { useUserId, useUserRole, formatDateStringNoDay } from "../../util_functions/helpers";
import {
  FaAngleLeft,
  FaCheck,
  FaSyncAlt,
  FaCircle,
} from "react-icons/fa";
import axios from "axios";
import config from "../../config/config";
import AgreementDetails from "../ps-account/agreements/AgreementDetails";
import { RotatingLines } from "react-loader-spinner";


const AgreementArea = () => {
  const userId = useUserId();
  const userType = useUserRole();
  const { agreementId } = useParams();
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { listAgreementsByUser, getByUserAndAgreement, markAgreementViewed, loading } = useAgreement();
  const mainContainerRef = useRef(null);
  const leftContainerRef = useRef(null);
  const rightContainerRef = useRef(null);
  const navigate = useNavigate();
  const baseURL = config.API_BASE_URL;

  const selectedAgreementRef = useRef(null);
  const agreementsPerPage = 6;
  const basePath =
    userType === "worker"
      ? "/account/ps-dashboard"
      : "/account/fs-dashboard/manage-workers";
  const allowedUserTypes = ["worker", "client"];

  const stepLabels = ["Sent", "Viewed", "Signed", "Active", "Done"];

  useEffect(() => {
  if (!userType || !userId) return;

  listAgreementsByUser(userType, userId)
    .then((list) => {
      console.log(
        "🔍 listAgreementsByUser result:",
        list.map(a => ({
          agreementId:     a.agreementId,
          status:          a.status,
          executionStage:  a.executionStage,
        }))
      );

      const sorted = [...list].sort((a, b) => b.agreementDate - a.agreementDate);
      setAgreements(sorted);
    })
    .catch((err) => {
      console.error("Error loading agreements:", err);
    });
}, [listAgreementsByUser, userType, userId]);


  useEffect(() => {
    if (!agreementId || !userType || !userId) return;

    (async () => {
      try {
        const data = await getByUserAndAgreement(userType, userId, agreementId);
        setSelectedAgreement(data);

        const updated = await markAgreementViewed(userType, userId, agreementId);

        setSelectedAgreement(updated);
      } catch (err) {
        console.error("Error loading or marking viewed:", err);
      }
    })();
  }, [
    agreementId,
    userType,
    userId,
    getByUserAndAgreement,
    markAgreementViewed
  ]);

  const handleAgreementClick = async (agreement) => {
    navigate(
      `${basePath}/agreements/${agreement.agreementId}`
    );
  };

  const handleBackToList = () => {
    setSelectedAgreement(null);
    navigate(`${basePath}/agreements`);
  };

  const getStatusLabel = agreement => {
    const { status, executionStage } = agreement;
    console.log("getStatusLabel:", {
    status: agreement.status,
    executionStage: agreement.executionStage
  });
  if (agreement.executionStage === "ACTIVE") {
    return <span className="agreement-status active">ACTIVE</span>;
  }
  if (["COMPLETED","TERMINATED","EXPIRED"].includes(agreement.executionStage)) {
    return <span className="agreement-status completed">COMPLETED</span>;
  }

  switch (agreement.status) {
    case "PARTIALLY_SIGNED_PENDING":
      return (
        <span className="agreement-status partially-signed-pending">
          PARTIALLY SIGNED (Pending)
        </span>
      );
    case "VIEWED_PENDING":
      return (
        <span className="agreement-status viewed-pending">
          VIEWED (Pending)
        </span>
      );
    case "FULLY_SIGNED":
      return <span className="agreement-status signed">FULLY SIGNED</span>;
    default:
      return <span className="agreement-status unknown">UNKNOWN</span>;
  }
};

  const indexOfLastAgreement = currentPage * agreementsPerPage;
  const indexOfFirstAgreement = indexOfLastAgreement - agreementsPerPage;
  const currentAgreements = agreements.slice(
    indexOfFirstAgreement,
    indexOfLastAgreement
  );
  console.log("Current Agreements for pagination:", currentAgreements);

  const totalPages = Math.ceil(agreements.length / agreementsPerPage);
  const maxPageNumbersToShow = 5;
  const halfPageNumbersToShow = Math.floor(maxPageNumbersToShow / 2);

  const startPage = Math.max(1, currentPage - halfPageNumbersToShow);
  const endPage = Math.min(totalPages, currentPage + halfPageNumbersToShow);

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePageChange = (action) => {
    if (action === "first") {
      setCurrentPage(1);
    } else if (action === "last") {
      setCurrentPage(totalPages);
    } else if (action === "previous" && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    } else if (action === "next" && currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    } else if (typeof action === "number") {
      setCurrentPage(action);
    }
  };

  const renderAgreementListItem = (agreement) => {
    const completed = [
      true,  
      agreement.status !== "PARTIALLY_SIGNED_PENDING",
      agreement.status === "FULLY_SIGNED",
      agreement.executionStage !== "PENDING",
  ["COMPLETED","TERMINATED","EXPIRED"].includes(agreement.executionStage),
    ];

    return (
      <div className="agreement-item-card">
        <h3 className="agreement-title">
            {agreement.jobTitle.length > 30
              ? `${agreement.jobTitle.slice(0, 32)}…`
              : agreement.jobTitle}
          </h3>

          <div className="simple-step-tracker">
          {stepLabels.map((label, i) => (
            <React.Fragment key={i}>
              <div className="step-item">
                <div className={`progress-step-circle ${completed[i] ? "done" : ""}`}>
                  {completed[i] && <FaCheck />}
                </div>
                <div className="step-label">{label}</div>
              </div>
              {i < stepLabels.length - 1 && (
                <div
                  className={`step-connector ${completed[i] ? "done" : ""}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="agreement-content">
          <div className="agreement-meta">
            <div className="agreement-meta-content">
              <span>Submitted<span className="user-dashboard-arrow">➤</span>{formatDateStringNoDay(agreement.agreementDate)}</span>
            </div>
            <div className="agreement-meta-content">
              <span>Client<span className="user-dashboard-arrow">➤</span><strong>{agreement.clientName}</strong></span>
            </div>

            <div className="agreement-meta-content">
              <span>Worker<span className="user-dashboard-arrow">➤</span><strong>{agreement.workerName}</strong></span>
            </div>
            <div className="agreement-meta-content">
              <span>Location<span className="user-dashboard-arrow">➤</span><strong>{agreement.jobLocation}</strong></span>
            </div>
            <div className="agreement-meta-content">
              <span>Status<span className="user-dashboard-arrow">➤</span></span>
              {getStatusLabel(agreement)}
            </div>
            <div className="agreement-meta-content">
              <span>Completed<span className="user-dashboard-arrow">➤</span>
              {formatDateStringNoDay(agreement.agreementDate)}</span>
            </div>
          </div>
        </div>

      </div>
    );
  };

  return (
    <div
      ref={mainContainerRef}
      className={`agreement-area-container ${selectedAgreement ? "expanded" : ""
        }`}
    >
      <div ref={leftContainerRef} className="agreement-area-left-container">

        <div className="agreement-area-left-div">
          {selectedAgreement && (
            <div className="agreement-details-back" onClick={handleBackToList}>
              <FaAngleLeft className="agreement-details-back-icon" />
            </div>
          )}
          {loading ? (
            <div className="jobs-loading-spinner-container">
              <RotatingLines
                visible={true}
                height="56"
                width="56"
                strokeWidth="1"
                animationDuration="0.75"
                ariaLabel="loading-jobs"
              />
            </div>
          ) : agreements.length === 0 ? (
            <div className="no-jobs-placeholder">
              <img
                src="/images/no_results_found.png"
                alt="No results found"
                className="no-jobs-image"
              />
              <p>No Agreements found.</p>
            </div>
          ) : (
            <ul className="agreement-list">
              {selectedAgreement && (
                <li
                  key={selectedAgreement.agreementId}
                  className="agreement-list-item selected"
                  onClick={() => handleAgreementClick(selectedAgreement)}
                >
                  {renderAgreementListItem(selectedAgreement)}
                </li>
              )}
              {currentAgreements
                .filter(
                  (agreement) =>
                    agreement.agreementId !== selectedAgreement?.agreementId
                )
                .map((agreement) => (
                  <li
                    key={agreement.agreementId}
                    className={`agreement-list-item ${selectedAgreement &&
                      selectedAgreement.agreementId === agreement.agreementId
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => handleAgreementClick(agreement)}
                  >
                    {renderAgreementListItem(agreement)}
                  </li>
                ))}
            </ul>
          )}
        </div>
        {agreements.length >= 10 && (
          <div className="active-jobs-pagination">
            <button
              onClick={() => handlePageChange("first")}
              disabled={currentPage === 1}
            >
              &laquo;
            </button>
            <button
              onClick={() => handlePageChange("previous")}
              disabled={currentPage === 1}
            >
              &lt;
            </button>
            <span>
              Page <strong>{currentPage}</strong> of{" "}
              <strong>{totalPages > 99 ? "99+" : totalPages}</strong>
            </span>
            <button
              onClick={() => handlePageChange("next")}
              disabled={currentPage >= totalPages}
            >
              &gt;
            </button>
            <button
              onClick={() => handlePageChange("last")}
              disabled={currentPage >= totalPages}
            >
              &raquo;
            </button>
          </div>
        )}

      </div>
      <div ref={rightContainerRef} className="agreement-area-right-container">
        <div ref={selectedAgreementRef} className="agreement-area-right-div">
          {selectedAgreement && (
            <AgreementDetails agreement={selectedAgreement} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AgreementArea;
